import React from 'react';
import { t } from '@lingui/macro';
import { Button, Dialog, Table3, IconButton, TableSettings, Table3ColumnProps } from '@economic/taco';
import { useCurrentUser, useLocalStorage, useRouteAuthorisation } from '@economic/react-app';
import { AccountMultipleDimension } from '~/types/internal-api';
import CreateAccountForm from '../forms/Accounts/CreateAccount';
import { ViewProps } from './types';
import View from '../shared/components/View/View';
import { AccountType, VatAccount } from '../../types';
import { useAccounts } from '../shared/hooks/masterdata/useAccounts';
import { findRowActiveIndex, isHeading } from './utils/accountsHelpers';

export interface DepartmentModel {
    departmentNumber: number;
    name: string;
    access: boolean;
}
// Suffixed "Model" because there is a DOM APIs Account interface
export interface AccountModel {
    access: boolean;
    balance: number;
    isBlockedForDirectEntries: boolean;
    isDefaultCredit: boolean;
    isDepartmentMandatory: boolean;
    isUnitMandatory: boolean;
    name: string;
    number: number;
    type: AccountType;
    department?: DepartmentModel;
    dimensions: AccountMultipleDimension[];
    contraAccount?: AccountModel;
    vatAccount?: VatAccount;
    currency?: string;
    assetGroupNumber?: number;
}

interface Props extends ViewProps<AccountModel> {
    disableCreate?: boolean;
    isBlockedForDirectEntries?: boolean;
    selectedAccountNumber?: number;
    modalDraggable?: boolean;
    showAllAccountTypes?: boolean;
    showAllAccounts?: boolean;
    allowHeadingClick?: boolean;
    showSaveAndApply?: boolean;
}

const Accounts = ({
    disableCreate = false,
    isBlockedForDirectEntries,
    onCreated: handleCreated,
    onSelected: handleSelected,
    selectedAccountNumber,
    modalDraggable,
    showAllAccountTypes = false,
    showAllAccounts = false,
    allowHeadingClick = false,
    showSaveAndApply = false,
}: Props) => {
    const [hasJournalAccess] = useRouteAuthorisation('/accounting');
    const user = useCurrentUser();
    const settingsKey = `taco.${user.agreement.number}_${user.id}.table.select-account-table.settings`;
    const [tableSettings] = useLocalStorage<TableSettings>(settingsKey, {});
    const { data: accounts, refetch } = useAccounts({
        showAllAccounts,
        showAllAccountTypes,
        showHeadingAccounts: true,
        isBlockedForDirectEntries,
    });
    const [rowActiveIndex, setRowActiveIndex] = React.useState<number | undefined>(
        findRowActiveIndex(accounts, selectedAccountNumber, tableSettings.sorting)
    );

    const showLedger = (accountNumber: number) => {
        window.top?.firebirdTask.then(
            () =>
                window.top?.PopUp2(
                    `/generelt/dataedit.asp?popup=yes&form=8&skjultrans=1&kladde=1&kontonr=${accountNumber}`,
                    820,
                    400
                )
        );
    };

    const ledgerCardButton = (row: AccountModel) =>
        isHeading(row.type.number) ? null : (
            <IconButton
                icon="entry-type-customer-invoice"
                aria-label={t`Ledger card`}
                onClick={ev => {
                    ev.stopPropagation();
                    showLedger(row.number);
                }}
                tooltip={t`Ledger card`}
            />
        );

    const renderer = ({ formattedValue, row }) => {
        let rowClassName = '';
        if (isHeading(row.type.number)) {
            if (allowHeadingClick) {
                rowClassName = 'font-bold';
            } else {
                rowClassName = 'font-bold pointer-events-none';
            }
        }
        return <div className={rowClassName}>{formattedValue}</div>;
    };

    const columnDefinitions: Table3ColumnProps<AccountModel>[] = [
        {
            accessor: 'number',
            header: t`No.`,
            defaultWidth: 75,
            sort: 'asc',
            renderer,
        },
        {
            accessor: 'name',
            header: t`Name`,
            align: 'left',
            renderer,
        },
        {
            accessor: 'type.name' as any,
            header: t`Type`,
            defaultWidth: 150,
            renderer,
        },
    ];

    return (
        <View>
            <View.Title>{t`Chart of accounts`}</View.Title>
            <View.Content tabIndex={0}>
                <Table3<AccountModel>
                    id="select-account-table"
                    data={accounts}
                    preset="complex"
                    enableFiltering={false}
                    enableRowActive
                    defaultRowActiveIndex={rowActiveIndex}
                    onRowClick={row => {
                        if (!isHeading(row.type.number) || allowHeadingClick) {
                            handleSelected(row);
                        }
                    }}
                    // quite useless since we don't use the updated rowActiveIndex value inside Table3
                    onChangeSettings={settings => {
                        setRowActiveIndex(findRowActiveIndex(accounts, selectedAccountNumber, settings.sorting));
                    }}
                    rowActions={hasJournalAccess ? [ledgerCardButton] : []}
                    toolbarLeft={
                        disableCreate ? undefined : (
                            <Button
                                appearance="primary"
                                dialog={props => (
                                    <Dialog size="md" draggable={modalDraggable} {...props}>
                                        <Dialog.Content aria-label={t`FORM_ACCOUNTS_CREATETEXT`}>
                                            {({ close }) => (
                                                <CreateAccountForm
                                                    modalDraggable={modalDraggable}
                                                    onCancel={close}
                                                    showSaveAndApply={showSaveAndApply}
                                                    onSubmitted={account => {
                                                        refetch();
                                                        if (handleCreated) {
                                                            handleCreated(account);
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Dialog.Content>
                                    </Dialog>
                                )}>
                                {t`New account`}
                            </Button>
                        )
                    }>
                    {columnDefinitions.map(column => (
                        <Table3.Column<AccountModel> key={column.accessor} {...column} />
                    ))}
                </Table3>
            </View.Content>
        </View>
    );
};

export default Accounts;
